import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  Dialog,
  IconButton,
  Button,
  Typography,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { testimonials } from '../data/testimonials';

const olevelImages = testimonials.filter((t) => t.image.includes('olevel'));
const otherImages = testimonials.filter((t) => !t.image.includes('olevel'));

const TestimonialsPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const allImages = [...olevelImages, ...otherImages];

  const handleOpen = (image) => {
    const idx = allImages.findIndex((t) => t.id === image.id);
    setCurrentIndex(idx);
    setLightboxOpen(true);
  };

  const handleClose = () => {
    setLightboxOpen(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box>
      <HeroSection
        title="Testimonials"
        subtitle="See what our students and parents have to say"
      />

      {/* O-Level 2025 Highlight Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, #FF6F00 0%, #FFA040 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Chip
              icon={<EmojiEventsIcon />}
              label="NEW"
              sx={{
                bgcolor: '#fff',
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.85rem',
                mb: 2,
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#fff',
                mb: 1,
                fontSize: { xs: '1.8rem', md: '2.4rem' },
              }}
            >
              O-Level 2025 Results
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontWeight: 400,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Congratulations to our students on their excellent O-Level Chinese results!
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {olevelImages.map((testimonial) => (
              <Grid item xs={6} sm={4} md key={testimonial.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    border: '3px solid rgba(255,255,255,0.4)',
                    '&:hover img': {
                      transform: 'scale(1.05)',
                    },
                    '&:hover': {
                      borderColor: '#fff',
                    },
                    transition: 'border-color 0.3s ease',
                  }}
                  onClick={() => handleOpen(testimonial)}
                >
                  <CardMedia
                    component="img"
                    image={testimonial.image}
                    alt={`O-Level 2025 Result`}
                    sx={{
                      height: { xs: 200, sm: 250, md: 280 },
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Other Testimonials */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="More Testimonials"
            subtitle="Click on any image to view it in full size"
          />

          <Grid container spacing={3}>
            {otherImages.map((testimonial) => (
              <Grid item xs={6} sm={4} md={3} key={testimonial.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    '&:hover img': {
                      transform: 'scale(1.05)',
                    },
                  }}
                  onClick={() => handleOpen(testimonial)}
                >
                  <CardMedia
                    component="img"
                    image={testimonial.image}
                    alt={`Testimonial ${testimonial.id}`}
                    sx={{
                      height: { xs: 180, sm: 220, md: 260 },
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/contact"
              startIcon={<ContactMailIcon />}
            >
              Join Our Students
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: { xs: -8, md: -50 },
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>

          <Box
            component="img"
            src={allImages[currentIndex]?.image}
            alt={`Testimonial ${allImages[currentIndex]?.id}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              borderRadius: 2,
              objectFit: 'contain',
            }}
          />

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: -8, md: -50 },
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              zIndex: 1,
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
};

export default TestimonialsPage;
